import useTheme from "../../../../common/hooks/useTheme";
import React from "react";
import { Skeleton } from "@mui/material";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";

export const CoreMetricCardType = {
  metabolicRate: "metabolism",
  fatBurn: "fat_burn_efficiency",
};

type Props = {
  title: string;
  type: string;
  isLoadingDataNut: boolean;
};

const CoreMetricCard = ({ title, type, isLoadingDataNut }: Props) => {
  const theme = useTheme();

  const homeData = useSelector(
    (state: RootStatePlatform) => state.platform.dataNutServer.overview_tab
  );

  if (isLoadingDataNut) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          flex: 1,
          height: "160px",
          bgcolor: theme.colorTokens.N[30],
          borderRadius: "8px",
        }}
        animation={"pulse"}
      />
    );
  }

  const data = homeData.pnoe_metrics.metrics.find((el) => el.name === type);

  const value = data ? data?.current_value - data?.initial_value : 0;

  return (
    <div style={styles.coreMetricContainer(theme)}>
      <div>
        <div style={styles.coreMetricTitle(theme)}>{title}</div>
        <div style={styles.coreMetricsValueContainer}>
          <div style={styles.coreMetricValue(theme)}>
            {value}
            <span style={styles.coreMetricUnit(theme)}>%</span>
          </div>
          {Number(value) !== 0 && (
            <div style={styles.coreMetricValueIconContainer(theme)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                style={{
                  transform: Number(value) > 0 ? "" : "rotate(180deg)",
                }}
              >
                <g clipPath="url(#clip0_5867_111209)">
                  <path
                    d="M10.2788 4.21373L6.52885 0.46373C6.3882 0.323127 6.19747 0.244141 5.9986 0.244141C5.79972 0.244141 5.60899 0.323127 5.46835 0.46373L1.71835 4.21373C1.58173 4.35518 1.50613 4.54463 1.50784 4.74128C1.50955 4.93793 1.58843 5.12604 1.72748 5.26509C1.86654 5.40415 2.05465 5.48303 2.2513 5.48474C2.44794 5.48644 2.63739 5.41085 2.77885 5.27423L5.2486 2.80448V11.494C5.2486 11.6929 5.32761 11.8837 5.46827 12.0243C5.60892 12.165 5.79968 12.244 5.9986 12.244C6.19751 12.244 6.38827 12.165 6.52893 12.0243C6.66958 11.8837 6.7486 11.6929 6.7486 11.494V2.80448L9.21835 5.27423C9.3598 5.41085 9.54925 5.48644 9.7459 5.48474C9.94254 5.48303 10.1307 5.40415 10.2697 5.26509C10.4088 5.12604 10.4876 4.93793 10.4894 4.74128C10.4911 4.54463 10.4155 4.35518 10.2788 4.21373Z"
                    fill={
                      Number(value) > 0
                        ? theme.colors_ds.SUCCESS_ICON_MAIN
                        : theme.colors_ds.DANGER_GRAPH_DEFICIT
                    }
                  />
                </g>
              </svg>
            </div>
          )}
        </div>
      </div>
      <div style={styles.coreMetricsSection}>
        <div style={styles.coreMetricsItems(theme)}>
          <div style={styles.coreMetricsItemsTitle(theme)}>Estimation</div>
          <div style={styles.coreMetricsItemsValue(theme)}>
            {data?.current_value}
            <span style={{ fontFamily: "var(--font-Medium)" }}>%</span>
          </div>
        </div>
        <div style={styles.coreMetricsItems(theme)}>
          <div style={styles.coreMetricsItemsTitle(theme)}>Latest test</div>
          <div style={styles.coreMetricsItemsValue(theme)}>
            {data?.initial_value}
            <span style={{ fontFamily: "var(--font-Medium)" }}>%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoreMetricCard;

const styles = {
  coreMetricContainer: (theme: any) => ({
    flex: 1,
    padding: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  coreMetricsSection: { display: "flex", gap: "8px" },
  coreMetricTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    lineHeight: "19.6px",
  }),
  coreMetricsValueContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    height: "28px",
  },
  coreMetricValue: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  coreMetricUnit: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  coreMetricValueIconContainer: (theme: any) => ({
    width: "24px",
    height: "24px",
    backgroundColor: theme.colorTokens.N[40],
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  }),
  coreMetricsItems: (theme: any) => ({
    borderRadius: "8px",
    padding: "8px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    flex: 1,
  }),
  coreMetricsItemsTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  coreMetricsItemsValue: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
